import React, { useEffect, useState } from 'react';

import { IChangeEvent } from '@rjsf/core';
import Form from '@rjsf/mui';
import type { RJSFSchema } from '@rjsf/utils';
import validator from '@rjsf/validator-ajv8';

import { Alert, Box, Link, Stack, Typography } from '@mui/material';

import { buttonStates } from '~/components/Common/Forms/states/submitButton';

import { PROPERTY_CHANNEL_MANAGERS } from '~/consts/reservation';

import ReservationService from '~/services/ReservationService';

import getErrorMessage from '~/utils/getErrorMessage';

import {
  DEFAULT_FLASH_TAX_FEE_PRODUCT_TYPE,
  TAX_BREAKDOWN_SOURCE_URL,
  getTaxFeePrefillItems,
  withDefaultTaxFeeProductType,
} from './taxRules';

type TaxFeeItem = {
  additional_tax: boolean;
  exclude_flash_pay_at_property: boolean;
  exclude_from_cm_rate: boolean;
  name: string;
  payable_at_property: boolean;
  per_person: boolean;
  product_type: string;
  type: string;
  unit: {
    amount?: number;
    currency?: string;
    value?: number;
  };
};

type SavedTaxFeeItem = {
  additional_tax?: boolean;
  currency?: string | null;
  exclude_flash_pay_at_property?: boolean;
  exclude_from_cm_rate?: boolean;
  name?: string | null;
  payable_at_property?: boolean;
  per_person?: boolean;
  product_type?: string | null;
  type?: string | null;
  unit?: string | null;
  value?: number | null;
};

type TaxSettings = {
  all_taxes_and_fees_as_non_commssionable: boolean;
  channel_manager_reservation_rate: string;
  commission_statement: boolean;
  exclude_flash_pay_at_property: boolean;
  gstin: string;
  include_tax_amount_into_before_amount?: boolean;
  send_daily_override_rates: boolean;
  taxes_payable_at_property: boolean;
};

type SavedPropertyTaxes = Partial<TaxSettings> & {
  channel_manager?: string | null;
  city?: string | null;
  country?: string | null;
  country_code?: string | null;
  geo_country_code?: string | null;
  geo_data?: {
    administrative_area_level_1?: string | null;
    administrative_area_level_2?: string | null;
    administrative_area_level_3?: string | null;
    administrative_area_level_4?: string | null;
    administrative_area_level_5?: string | null;
    country?: string | null;
    country_code?: string | null;
    locality?: string | null;
  };
  location_heading?: string | null;
  location_subheading?: string | null;
  taxes_and_fees?: Array<SavedTaxFeeItem> | null;
};

type TaxFeesFormData = TaxSettings & {
  taxes_and_fees: Array<TaxFeeItem>;
};

type Props = {
  disabled?: boolean;
  formId: string;
  onSaved: () => void | Promise<void>;
  onSavingChange?: (isSaving: boolean) => void;
  propertyId?: string;
};

const uiSchema = {
  gstin: {
    'ui:placeholder': 'Enter GSTIN',
    classNames: 'gstin-block',
  },
  commission_statement: {
    classNames: 'gstin-block',
  },
  taxes_and_fees: {
    items: {
      classNames: 'tax-block',
    },
  },
};

const mapSavedTaxFee = (item: SavedTaxFeeItem): TaxFeeItem => ({
  additional_tax: !!item.additional_tax,
  exclude_flash_pay_at_property: !!item.exclude_flash_pay_at_property,
  exclude_from_cm_rate: !!item.exclude_from_cm_rate,
  name: item.name ?? '',
  payable_at_property: !!item.payable_at_property,
  per_person: !!item.per_person,
  product_type: item.product_type ?? DEFAULT_FLASH_TAX_FEE_PRODUCT_TYPE,
  type: item.type ?? 'night',
  unit:
    item.unit === 'amount'
      ? {
          amount: item.value ?? 0,
          currency: item.currency ?? '',
        }
      : {
          value: item.value ?? 0,
        },
});

const mapTaxSettings = (property: SavedPropertyTaxes): TaxSettings => ({
  all_taxes_and_fees_as_non_commssionable: !!property.all_taxes_and_fees_as_non_commssionable,
  channel_manager_reservation_rate: property.channel_manager_reservation_rate ?? 'default',
  commission_statement: !!property.commission_statement,
  exclude_flash_pay_at_property: !!property.exclude_flash_pay_at_property,
  gstin: property.gstin ?? '',
  include_tax_amount_into_before_amount: property.include_tax_amount_into_before_amount,
  send_daily_override_rates: !!property.send_daily_override_rates,
  taxes_payable_at_property: !!property.taxes_payable_at_property,
});

function TaxesFeesStep({ disabled = false, formId, onSaved, onSavingChange, propertyId }: Props) {
  const [schema, setSchema] = useState<RJSFSchema | null>(null);
  const [formData, setFormData] = useState<TaxFeesFormData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [saveState, setSaveState] = useState(buttonStates.default);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!propertyId) {
      return;
    }

    setIsLoading(true);
    setErrorMessage(null);
    setSaveState(buttonStates.default);

    let ignore = false;

    Promise.all([
      ReservationService.getProperty(propertyId),
      ReservationService.getPropertyTaxesAndFeesSchema(propertyId),
    ])
      .then(([propertyData, schemaData]) => {
        if (ignore) {
          return;
        }

        const loadedProperty = propertyData.result as SavedPropertyTaxes;
        const existingItems = (loadedProperty.taxes_and_fees ?? []).map(mapSavedTaxFee);
        const prefillItems = getTaxFeePrefillItems(
          loadedProperty.country ||
            loadedProperty.geo_data?.country ||
            loadedProperty.location_subheading ||
            loadedProperty.country_code ||
            loadedProperty.geo_country_code ||
            loadedProperty.geo_data?.country_code,
          loadedProperty.city || loadedProperty.geo_data?.locality || loadedProperty.location_heading,
          [
            loadedProperty.location_subheading,
            loadedProperty.geo_data?.administrative_area_level_1,
            loadedProperty.geo_data?.administrative_area_level_2,
            loadedProperty.geo_data?.administrative_area_level_3,
            loadedProperty.geo_data?.administrative_area_level_4,
            loadedProperty.geo_data?.administrative_area_level_5,
          ]
            .filter(Boolean)
            .join(' '),
        );
        const schema = schemaData.put.body.schema;
        const {
          all_taxes_and_fees_as_non_commssionable,
          channel_manager_reservation_rate,
          commission_statement,
          exclude_flash_pay_at_property,
          gstin,
          include_tax_amount_into_before_amount,
          send_daily_override_rates,
          taxes_and_fees,
          taxes_payable_at_property,
        } = schema.properties;

        setSchema({
          type: 'object',
          properties: {
            commission_statement,
            gstin,
            taxes_payable_at_property,
            all_taxes_and_fees_as_non_commssionable,
            ...(loadedProperty.channel_manager === PROPERTY_CHANNEL_MANAGERS.derbysoft
              ? { include_tax_amount_into_before_amount }
              : {}),
            exclude_flash_pay_at_property,
            send_daily_override_rates,
            channel_manager_reservation_rate,
            taxes_and_fees: withDefaultTaxFeeProductType(taxes_and_fees),
          },
          dependencies: schema.dependencies,
        });
        setFormData({
          ...mapTaxSettings(loadedProperty),
          taxes_and_fees: existingItems.length === 0 ? prefillItems : existingItems,
        });
      })
      .catch((error) => {
        if (!ignore) {
          setErrorMessage(getErrorMessage(error));
        }
      })
      .finally(() => {
        if (!ignore) {
          setIsLoading(false);
        }
      });

    return () => {
      ignore = true;
    };
  }, [propertyId]);

  const handleChange = ({ formData }: IChangeEvent<TaxFeesFormData>) => {
    setErrorMessage(null);
    setSaveState(buttonStates.default);
    setFormData(formData);
  };

  const handleSubmit = async ({ formData }: IChangeEvent<TaxFeesFormData>) => {
    if (!propertyId) {
      return;
    }

    setErrorMessage(null);
    setSaveState(buttonStates.saving);
    onSavingChange?.(true);

    try {
      await ReservationService.updatePropertyTaxesAndFees(
        {
          taxes_and_fees: formData.taxes_and_fees,
          commission_statement: formData.commission_statement,
          taxes_payable_at_property: formData.taxes_payable_at_property,
          all_taxes_and_fees_as_non_commssionable: formData.all_taxes_and_fees_as_non_commssionable,
          include_tax_amount_into_before_amount: formData.include_tax_amount_into_before_amount,
          gstin: formData.commission_statement ? formData.gstin : '',
          exclude_flash_pay_at_property: formData.exclude_flash_pay_at_property,
          channel_manager_reservation_rate: formData.channel_manager_reservation_rate,
          send_daily_override_rates: formData.send_daily_override_rates,
        },
        propertyId,
      );
      setSaveState(buttonStates.saved);
      await onSaved();
      onSavingChange?.(false);
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
      setSaveState(buttonStates.failed);
      onSavingChange?.(false);
    }
  };

  if (!propertyId) {
    return <Alert severity="warning">Select or create a property before editing taxes and fees.</Alert>;
  }

  if (isLoading) {
    return <Typography>Loading...</Typography>;
  }

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          Please note:
        </Typography>
        <Box component="ul" sx={{ m: 0, pl: 3 }}>
          <Typography component="li" variant="body2">
            Taxes/fees can be per night or per stay
          </Typography>
          <Typography component="li" variant="body2">
            Each entry will be applied to the gross price
          </Typography>
          <Typography component="li" variant="body2">
            Ordering does not affect the final calculation
          </Typography>
          <Typography component="li" variant="body2">
            Channel manager taxes & fees will take precedence over property taxes & fees
          </Typography>
          <Typography component="li" variant="body2">
            Taxes marked as "Channel Manager Additional Tax" will be added to the rates sent by the channel manager
          </Typography>
          <Typography component="li" variant="body2">
            "Channel Manager Additional Tax" taxes that are also marked as "Send Tax-Exclusive Rates to CM" will be
            exclude from the rates sent to the channel manager when a reservation is made
          </Typography>
        </Box>
      </Box>

      <Typography variant="body2" color="text.secondary">
        Tax information source:{' '}
        <Link href={TAX_BREAKDOWN_SOURCE_URL} target="_blank" rel="noopener noreferrer">
          Tax Breakdown by Country
        </Link>
      </Typography>

      {schema && formData && (
        <Form
          id={formId}
          schema={schema}
          uiSchema={uiSchema}
          formData={formData}
          disabled={disabled}
          onChange={handleChange}
          onError={() => {
            setSaveState(buttonStates.failed);
            onSavingChange?.(false);
          }}
          onSubmit={handleSubmit}
          validator={validator}
        >
          <></>
        </Form>
      )}

      {!!errorMessage && <Alert severity="error">{errorMessage}</Alert>}
    </Stack>
  );
}

export default TaxesFeesStep;
